// purchased-policies.component.ts
import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PolicyService, PolicySearchParams, PolicyStatus } from '../../shared/services/policy.service';
import { Policy } from '../../models/policy.interface';
import { PaginatedPolicyResponse } from '../../models/policy.interface';
import { Router } from '@angular/router';
import { LoaderComponent } from '../../components/loader/loader.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CompanyService } from '../../shared/services/company.service';

@Component({
  selector: 'app-purchased-policies',
  standalone: true,
  imports: [CommonModule, FormsModule, LoaderComponent, TranslateModule],
  templateUrl: './purchased-policies.component.html',
  styleUrls: ['./purchased-policies.component.scss'],
})
export class PurchasedPoliciesComponent implements OnInit, OnDestroy {
  selectedTab: string = 'active';
  loading = false;
  error: string | null = null;
  currentPath: string = '';
  currentSelectedCompanyId: number = 0;
  currentLang: string = 'en';

  // Search functionality
  searchParams: PolicySearchParams = {};
  isSearching: boolean = false;
  private searchTimeout: any;
  
  // All policies (both original and filtered)
  allPolicies: Policy[] = [];
  activePolicies: Policy[] = [];
  renewPolicies: Policy[] = [];
  expiredPolicies: Policy[] = [];
  cancelledPolicies: Policy[] = [];
  pendingInsurance: Policy[] = [];

  // Available status options for search
  statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: PolicyStatus.ACTIVE, label: 'Active' },
    { value: PolicyStatus.RENEW, label: 'Renew' },
    { value: PolicyStatus.EXPIRED, label: 'Expired' },
    { value: PolicyStatus.CANCELLED, label: 'Cancelled' },
    { value: PolicyStatus.PENDING, label: 'Pending' },
  ];

  tabs = [
    { name: 'active', label: 'DASHBOARD.POLICIES.ACTIVE_POLICIES', count: 0 },
    { name: 'renew', label: 'DASHBOARD.POLICIES.RENEW_POLICIES', count: 0 },
  ];

  constructor(private router: Router, private policyService: PolicyService, private translateService: TranslateService, private companyService: CompanyService) {
    // Initialize current language
    this.currentLang = this.translateService.currentLang || this.translateService.getDefaultLang() || 'en';

    // Subscribe to language changes
    this.translateService.onLangChange.subscribe(event => {
      this.currentLang = event.lang;
    });
  }

  ngOnInit(): void {
    this.currentPath = this.router.url;
    if (this.currentPath.includes('renew-policy')) {
      this.selectedTab = 'renew';
    }
    console.log('Current path:', this.currentPath);
    this.companyService.currentSelectedCompany$.subscribe(companyId => {
      this.currentSelectedCompanyId = companyId;
      this.loadPolicies();
    });

  }

  ngOnDestroy(): void {
    // Clear search timeout to prevent memory leaks
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }

  private getUserIdFromLocalStorage(): string {
    const keycloakProfile = localStorage.getItem('keycloakUserProfile');
    if (keycloakProfile) {
      try {
        const profile = JSON.parse(keycloakProfile);
        return profile.id;
      } catch (error) {
        console.error('Error parsing Keycloak profile:', error);
        return '';
      }
    }
    return '';
  }

  loadPolicies(): void {
    this.loading = true;
    this.error = null;

    const userId = this.getUserIdFromLocalStorage();
    if (!userId) {
      this.error = 'User not authenticated. Please login again.';
      this.loading = false;
      return;
    }

    // Load all policy types from different endpoints since backend filters by status
    Promise.all([
      this.policyService.getActivePoliciesByUserId(userId, this.currentSelectedCompanyId).toPromise(),
      this.policyService.getExpiredPolicies(userId, this.currentSelectedCompanyId).toPromise(),
    ]).then(([activeResponse, renewedResponse]) => {
      this.activePolicies = activeResponse?.content || [];
      this.renewPolicies = renewedResponse?.content || [];

      this.updateTabCounts();
      this.loading = false;
      console.log('All policies loaded successfully');
    }).catch((error) => {
      console.error('Error loading policies:', error);
      if (error.status === 401) {
        this.error = 'You are not authorized to view these policies.';
      } else if (error.status === 500) {
        this.error = 'Server error occurred. Please try again later.';
      } else {
        this.error = this.translateService.instant('ERRORS.FAILED_LOAD_POLICIES');
      }
      this.loading = false;
    });
  }

  categorizePolicies(policies: Policy[]): void {
    // This method is now optional since backend handles filtering by status
    // Keeping for backward compatibility, but the new loadPolicies method
    // loads policies directly from status-specific endpoints
    const now = new Date();
    console.log('Categorizing policies:', policies);

    this.activePolicies = policies.filter(
      (policy) =>
        new Date(policy.endDate) > now &&
        policy.status?.toLowerCase() === 'active'
    );

    this.renewPolicies = policies.filter((policy) => {
      const endDate = new Date(policy.endDate); //2024,07,13
      const renewWindow = new Date();
      renewWindow.setDate(now.getDate() + 30); //2024,06,12
      return (
        endDate <= renewWindow && policy.status?.toLowerCase() === 'active'
      );
    });

    this.expiredPolicies = policies.filter(
      (policy) =>
        new Date(policy.endDate) <= now &&
        policy.status?.toLowerCase() !== 'cancelled'
    );

    this.cancelledPolicies = policies.filter(
      (policy) => policy.status?.toLowerCase() === 'cancelled'
    );

    this.pendingInsurance = policies.filter(
      (policy) => policy.status?.toLowerCase() === 'pending'
    );
  }

  updateTabCounts(): void {
    this.tabs = this.tabs.map((tab) => ({
      ...tab,
      count: this.getPoliciesForTab(tab.name).length,
    }));
  }

  getPoliciesForTab(tabName: string): Policy[] {
    // Return the categorized policies based on tab
    switch (tabName) {
      case 'active':
        return this.activePolicies;
      case 'renew':
        return this.renewPolicies;
      case 'expired':
        return this.expiredPolicies;
      default:
        return [];
    }
  }

  selectTab(tabName: string): void {
    this.selectedTab = tabName;
  }

  onPolicySelected(policy: Policy): void {
    // Handle policy selection (e.g., navigate to policy details)
    console.log('Selected policy:', policy);
  }

  getEmptyMessage(): string {
    // If there's an error message (like search failed or no results), show that
    if (this.error) {
      return this.error;
    }

    // If search is active and no criteria, show search prompt
    if (this.hasSearchCriteria()) {
      return 'No policies found matching your search criteria';
    }

    // Default tab-specific messages
    const tab = this.tabs.find((t) => t.name === this.selectedTab);
    if (tab) {
      const translatedLabel = this.translateService.instant(tab.label);
      return `No ${translatedLabel} Found`;
    }
    return 'No Policies Found';
  }

  searchPolicies(): void {
    if (!this.hasSearchCriteria()) {
      // If no search criteria, reload all policies
      this.loadPolicies();
      return;
    }

    this.loading = true;
    this.error = null;
    this.isSearching = true;

    this.policyService.searchPolicies(this.searchParams).subscribe({
      next: (response: PaginatedPolicyResponse) => {
        const searchResults = response.content || [];

        // If no results found, show empty state
        if (searchResults.length === 0) {
          this.error = 'No policies found matching your search criteria.';
          // Clear all policy arrays to show empty state
          this.activePolicies = [];
          this.renewPolicies = [];
          this.expiredPolicies = [];
          this.cancelledPolicies = [];
          this.pendingInsurance = [];
          this.updateTabCounts();
        } else {
          // Filter the search results into appropriate categories
          this.categorizePolicies(searchResults);
          this.updateTabCounts();
          this.error = null;
        }

        this.loading = false;
        this.isSearching = false;
        console.log('Search results:', response);
      },
      error: (error) => {
        console.error('Error searching policies:', error);
        this.error = 'Failed to search policies. Please try again.';
        // Clear all policy arrays to show empty state on error
        this.activePolicies = [];
        this.renewPolicies = [];
        this.expiredPolicies = [];
        this.cancelledPolicies = [];
        this.pendingInsurance = [];
        this.updateTabCounts();
        this.loading = false;
        this.isSearching = false;
      }
    });
  }

  clearSearch(): void {
    this.searchParams = {};
    this.error = null;
    this.isSearching = false;
    this.loadPolicies();
  }

  onSearchKeyup(): void {
    // Clear existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Set a new timeout to search after 500ms of no typing
    this.searchTimeout = setTimeout(() => {
      this.searchPolicies();
    }, 500);
  }

  hasSearchCriteria(): boolean {
    return !!(
      this.searchParams.searchTerm ||
      this.searchParams.companyName ||
      this.searchParams.payerName ||
      this.searchParams.policyNumber ||
      this.searchParams.status
    );
  }

  onStatusChange(event: any): void {
    const value = event.target.value;
    this.searchParams.status = value ? value as PolicyStatus : undefined;
  }

  purchaseNewPolicy(): void {
    // TODO: Navigate to policy purchase page
    this.router.navigate(['/dashboard/quotations']);
  }

  viewDetails(policy: Policy): void {
    // Navigate to policy details page
    if (policy.id) {
      this.router.navigate(['/dashboard/policy-details', policy.id]);
    } else {
      console.error('Policy ID is missing');
    }
  }

  getProviderInitials(providerName: string): string {
    const words = providerName.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return providerName.substring(0, 2).toUpperCase();
  }

  getProviderColor(providerName: string): string {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-red-500',
      'bg-yellow-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500',
    ];
    let hash = 0;
    for (let i = 0; i < providerName.length; i++) {
      hash = providerName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colorIndex = Math.abs(hash % colors.length);
    return colors[colorIndex];
  }

  getStatusClass(status?: string): string {
    if (!status) return 'bg-gray-100 text-gray-800';

    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'renewed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      case 'pending':
        return 'bg-orange-100 text-orange-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }
}
